import CommonModalBase from '@/components/common/CommonModal'
import CustomInput from '@/components/Returns/CustomInput'
import { MTCustomSelect } from '@/multi-territory/mt-components/mt-shared/MTCustomSelect'
import { MTMultiSelect } from '@/multi-territory/mt-components/mt-shared/MTMultiSelect'
import { COUNTRY_OPTIONS } from '@/multi-territory/mt-constants/mt-country-default'
import { assignEntityFormSchema } from '@/multi-territory/mt-schemas/mt-assign-entity-schema'
import {
  getEntityDropdownByCountry,
  getTanDropdownByEntities,
  getUserList,
} from '@/multi-territory/mt-services/mt-assign-entity-api'
import type {
  MTAssignEntityCreateRequest,
  MTEntityDropdownItem,
  MTTanDropdownItem,
  MTUserItem,
} from '@/multi-territory/mt-types/mt-admin'
import { Box, Button, Grid, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const CommonModal = CommonModalBase as any
const EntityInput = CustomInput as any
const EntitySelect = MTCustomSelect as any
const EntityMultiSelect = MTMultiSelect as any

interface CountryOption {
  uuid: string
  countryCode: string
  countryName: string
  taxRegistrationRequired?: boolean
}

interface FormErrors {
  userId?: string
  email?: string
  businessRole?: string
  countries?: string
  entityNames?: string
  taxIdentificationNumbers?: string
  general?: string
}

interface MTAdminAssignEntityFormProps {
  open: boolean
  onClose: () => void
  editingAssignment?: Record<string, unknown> | null
  onSubmit: (formData: {
    userId: number
    entityTanMasterDTOs: MTAssignEntityCreateRequest['entityTanMasterDTOs']
  }) => Promise<void>
  countryOptions: CountryOption[]
  /** Current user role - used to filter user dropdown (ADMIN sees only PREPARER users) */
  roleTypes?: string
}

export function MTAdminAssignEntityForm({
  open,
  onClose,
  editingAssignment = null,
  onSubmit,
  countryOptions: parentCountryOptions,
  roleTypes,
}: MTAdminAssignEntityFormProps) {
  // State
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>(
    parentCountryOptions.length > 0
      ? parentCountryOptions
      : COUNTRY_OPTIONS.map(item => ({
          uuid: item.uuid,
          countryCode: item.country_code,
          countryName: item.country_name,
        }))
  )

  // Get current user role directly from Redux (prop may be undefined on first render)
  const currentUserRole = useSelector((state: any) => state.user?.role)

  // User list from API
  const [userList, setUserList] = useState<MTUserItem[]>([])
  const [userLoading, setUserLoading] = useState(false)

  // Form data
  const [selectedUserDisplay, setSelectedUserDisplay] = useState('')
  const [selectedUserId, setSelectedUserId] = useState<number>(0)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [businessRole, setBusinessRole] = useState('')
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [selectedEntityUuids, setSelectedEntityUuids] = useState<string[]>([])
  const [selectedTanUuids, setSelectedTanUuids] = useState<string[]>([])

  // Dropdown data from API
  const [entityOptions, setEntityOptions] = useState<MTEntityDropdownItem[]>([])
  const [tanOptions, setTanOptions] = useState<MTTanDropdownItem[]>([])

  // Loading states
  const [entityLoading, setEntityLoading] = useState(false)
  const [tanLoading, setTanLoading] = useState(false)
  const [formLoading, setFormLoading] = useState(false)

  const [formErrors, setFormErrors] = useState<FormErrors>({})

  // Sync parent country options
  useEffect(() => {
    if (parentCountryOptions.length > 0) {
      setCountryOptions(parentCountryOptions)
    }
  }, [parentCountryOptions])

  // Fetch user list when role is available
  useEffect(() => {
    if (currentUserRole) {
      fetchUserList()
    }
  }, [currentUserRole])

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (editingAssignment) {
        setSelectedUserDisplay((editingAssignment.username as string) || '')
        setUsername((editingAssignment.username as string) || '')
        setEmail('')
        setBusinessRole('')
        setSelectedCountries([])
        setSelectedEntityUuids([])
        setSelectedTanUuids([])
      } else {
        resetForm()
      }
      setFormErrors({})
    }
  }, [open, editingAssignment])

  // Fetch entity dropdown when countries change
  useEffect(() => {
    if (selectedCountries.length > 0) {
      fetchEntityDropdown()
    } else {
      setEntityOptions([])
      setSelectedEntityUuids([])
      setTanOptions([])
      setSelectedTanUuids([])
    }
  }, [selectedCountries])

  // Fetch TAN dropdown when entities change
  useEffect(() => {
    if (selectedEntityUuids.length > 0) {
      fetchTanDropdown()
    } else {
      setTanOptions([])
      setSelectedTanUuids([])
    }
  }, [selectedEntityUuids])

  const fetchUserList = async () => {
    setUserLoading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: 1,
          pageSize: 100,
          sortField: 'lastModifiedDate',
          sortOrder: 'DESC',
          includeCount: true,
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList:
          currentUserRole === 'ADMIN'
            ? [{ fieldName: 'role', operator: 'EQUAL_TO', value: 'PREPARER' }]
            : [],
      }
      const response = await getUserList(payload)
      if (response.success && response.data?.content) {
        // Only show active users, and for ADMIN role: client-side filter to PREPARER only
        let activeUsers = response.data.content.filter(u => u.isActive)
        if (currentUserRole === 'ADMIN') {
          activeUsers = activeUsers.filter(u => u.role === 'PREPARER')
        }
        setUserList(activeUsers)
      }
    } catch {
      setUserList([])
    } finally {
      setUserLoading(false)
    }
  }

  const fetchEntityDropdown = async () => {
    const countryUuids = countryOptions
      .filter(c => selectedCountries.includes(c.countryCode))
      .map(c => c.uuid)

    if (countryUuids.length === 0) return

    setEntityLoading(true)
    try {
      const response = await getEntityDropdownByCountry(countryUuids)
      if (response.success && Array.isArray(response.data)) {
        setEntityOptions(response.data)
      } else {
        setEntityOptions([])
      }
    } catch {
      setEntityOptions([])
    } finally {
      setEntityLoading(false)
    }
  }

  const fetchTanDropdown = async () => {
    if (selectedEntityUuids.length === 0) return

    setTanLoading(true)
    try {
      const response = await getTanDropdownByEntities(selectedEntityUuids)
      if (response.success && Array.isArray(response.data)) {
        setTanOptions(response.data)
      } else {
        setTanOptions([])
      }
    } catch {
      setTanOptions([])
    } finally {
      setTanLoading(false)
    }
  }

  const resetForm = () => {
    setSelectedUserDisplay('')
    setSelectedUserId(0)
    setUsername('')
    setEmail('')
    setBusinessRole('')
    setSelectedCountries([])
    setSelectedEntityUuids([])
    setSelectedTanUuids([])
    setEntityOptions([])
    setTanOptions([])
    setFormErrors({})
  }

  // Handle user selection - auto-fills name, email, businessRole
  const handleUserSelect = (event: { target: { value: string } }) => {
    const selectedUsername = event.target.value
    const user = userList.find(u => u.username === selectedUsername)

    if (user) {
      setSelectedUserDisplay(user.username)
      setSelectedUserId(user.userId)
      setUsername(user.name)
      setEmail(user.emailId)
      setBusinessRole(user.role)
      setSelectedCountries([])
      setSelectedEntityUuids([])
      setSelectedTanUuids([])
      setEntityOptions([])
      setTanOptions([])
      setFormErrors(prev => ({
        ...prev,
        userId: '',
        email: '',
        businessRole: '',
        countries: '',
        entityNames: '',
        taxIdentificationNumbers: '',
      }))
    }
  }

  const handleCountryChange = (e: { target: { value: string[] } }) => {
    setSelectedCountries(e.target.value)
    setSelectedEntityUuids([])
    setSelectedTanUuids([])
    setFormErrors(prev => ({ ...prev, countries: '' }))
  }

  const handleEntityChange = (e: { target: { value: string[] } }) => {
    setSelectedEntityUuids(e.target.value)
    setSelectedTanUuids([])
    setFormErrors(prev => ({ ...prev, entityNames: '' }))
  }

  const handleTanChange = (e: { target: { value: string[] } }) => {
    setSelectedTanUuids(e.target.value)
    setFormErrors(prev => ({ ...prev, taxIdentificationNumbers: '' }))
  }

  const validateForm = (): boolean => {
    const result = assignEntityFormSchema.safeParse({
      selectedUserDisplay,
      email,
      businessRole,
      selectedCountries,
      selectedEntityUuids,
      selectedTanUuids,
    })

    if (result.success) {
      setFormErrors({})
      return true
    }

    const errors: FormErrors = {}
    result.error.issues.forEach(issue => {
      const field = issue.path[0] as string
      if (field === 'selectedUserDisplay') errors.userId = issue.message
      else if (field === 'email') errors.email = issue.message
      else if (field === 'businessRole') errors.businessRole = issue.message
      else if (field === 'selectedCountries') errors.countries = issue.message
      else if (field === 'selectedEntityUuids')
        errors.entityNames = issue.message
      else if (field === 'selectedTanUuids')
        errors.taxIdentificationNumbers = issue.message
    })

    setFormErrors(errors)
    return false
  }

  const handleFormSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setFormLoading(true)
    try {
      // Build entityTanMasterDTOs: group tanIds by entityMasterUuid
      const entityTanMap = new Map<string, (string | null)[]>()

      // Build a set of non-tax-required entity uuids
      // Check via tanOptions countryCode first, then fall back to selectedEntityUuids not in tanOptions at all
      const nonTaxEntityUuids = new Set(
        selectedEntityUuids.filter(entityUuid => {
          const tan = tanOptions.find(t => t.entityMasterUuid === entityUuid)
          if (tan) {
            const country = countryOptions.find(
              c => c.countryCode === tan.countryCode
            )
            return country?.taxRegistrationRequired === false
          }
          // No TAN entry at all — entity has no TANs, treat as non-tax-required
          return true
        })
      )

      selectedTanUuids.forEach(tanUuid => {
        const tanItem = tanOptions.find(t => t.uuid === tanUuid)
        if (tanItem) {
          const entityUuid = tanItem.entityMasterUuid
          if (!entityTanMap.has(entityUuid)) {
            entityTanMap.set(entityUuid, [])
          }
          // Only push tanUuid if tax registration is required for this entity
          if (!nonTaxEntityUuids.has(entityUuid)) {
            entityTanMap.get(entityUuid)!.push(tanUuid)
          }
        }
      })

      // Ensure all non-tax-required entities are included with empty tanIds
      nonTaxEntityUuids.forEach(entityUuid => {
        entityTanMap.set(entityUuid, [])
      })

      const entityTanMasterDTOs: MTAssignEntityCreateRequest['entityTanMasterDTOs'] =
        Array.from(entityTanMap.entries()).map(
          ([entityMasterUuid, tanIds]) => ({
            userId: selectedUserId,
            entityMasterUuid,
            tanIds,
          })
        )

      await onSubmit({ userId: selectedUserId, entityTanMasterDTOs })
      handleModalClose()
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      setFormErrors({ general: errMsg })
    } finally {
      setFormLoading(false)
    }
  }

  const handleModalClose = () => {
    resetForm()
    onClose()
  }

  // Derive display label for TAN options.
  // India (countryCode "IND") stores the TAN in
  // attributes.tax_deduction_account_number; other countries use
  // attributes.tax_identification_number.
  const getTanLabel = (tan: MTTanDropdownItem) => {
    const attrs = tan.attributes ?? {}
    const tinValue =
      attrs.tax_identification_number ||
      attrs.tax_deduction_account_number ||
      tan.uuid
    return `${tinValue} (${tan.entityName})`
  }

  return (
    <CommonModal
      open={open}
      onClose={handleModalClose}
      title="Assign entity"
      maxWidth="sm"
    >
      <>
        {/* Form */}
        <Box>
          <form onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
              {/* Row 1: User ID + User name */}
              <Grid size={6}>
                <EntitySelect
                  label="User ID"
                  required
                  value={selectedUserDisplay}
                  onChange={handleUserSelect}
                  options={userList}
                  getOptionLabel={(option: MTUserItem) => option.username}
                  getOptionValue={(option: MTUserItem) => option.username}
                  placeholder={userLoading ? 'Loading...' : 'Select user'}
                  error={!!formErrors.userId}
                  helperText={formErrors.userId}
                  disabled={userLoading}
                  searchable
                  searchPlaceholder="Search user"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <EntityInput
                  label="User name"
                  required
                  value={username}
                  disabled
                  placeholder="Auto-filled"
                  fullWidth
                />
              </Grid>

              {/* Row 2: Email ID + Business role */}
              <Grid size={6}>
                <EntityInput
                  label="Email ID"
                  required
                  value={email}
                  disabled
                  placeholder="Auto-filled"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <EntitySelect
                  label="Business role"
                  required
                  value={businessRole}
                  onChange={(e: { target: { value: string } }) =>
                    setBusinessRole(e.target.value)
                  }
                  options={[
                    { label: 'Preparer', value: 'PREPARER' },
                    { label: 'Admin', value: 'ADMIN' },
                    { label: 'IT Admin', value: 'IT_ADMIN' },
                  ]}
                  getOptionLabel={(opt: { label: string; value: string }) =>
                    opt.label
                  }
                  getOptionValue={(opt: { label: string; value: string }) =>
                    opt.value
                  }
                  placeholder="Select role"
                  error={!!formErrors.businessRole}
                  helperText={formErrors.businessRole}
                  disabled
                  fullWidth
                />
              </Grid>

              {/* Row 3: Country + Entity name */}
              <Grid size={6}>
                <EntityMultiSelect
                  label="Country"
                  required
                  value={selectedCountries}
                  onChange={handleCountryChange}
                  options={countryOptions}
                  getOptionLabel={(option: CountryOption) => option.countryName}
                  getOptionValue={(option: CountryOption) => option.countryCode}
                  placeholder="Select country"
                  error={!!formErrors.countries}
                  helperText={formErrors.countries}
                  searchable
                  searchPlaceholder="Search country"
                  fullWidth
                />
              </Grid>
              <Grid size={6}>
                <EntityMultiSelect
                  label="Entity name"
                  required
                  value={selectedEntityUuids}
                  onChange={handleEntityChange}
                  options={entityOptions}
                  getOptionLabel={(option: MTEntityDropdownItem) =>
                    option.entityName
                  }
                  getOptionValue={(option: MTEntityDropdownItem) => option.uuid}
                  placeholder={entityLoading ? 'Loading...' : 'Select entity'}
                  error={!!formErrors.entityNames}
                  helperText={formErrors.entityNames}
                  disabled={selectedCountries.length === 0 || entityLoading}
                  searchable
                  searchPlaceholder="Search entity"
                  fullWidth
                />
              </Grid>

              {/* Row 4: Tax identification number */}
              <Grid size={6}>
                <EntityMultiSelect
                  label="Tax identification number"
                  required
                  value={selectedTanUuids}
                  onChange={handleTanChange}
                  options={tanOptions}
                  getOptionLabel={getTanLabel}
                  getOptionValue={(option: MTTanDropdownItem) => option.uuid}
                  placeholder={tanLoading ? 'Loading...' : 'Select TIN/UEN'}
                  error={!!formErrors.taxIdentificationNumbers}
                  helperText={formErrors.taxIdentificationNumbers}
                  disabled={selectedEntityUuids.length === 0 || tanLoading}
                  searchable
                  searchPlaceholder="Search TIN"
                  fullWidth
                />
              </Grid>
            </Grid>

            {formErrors.general && (
              <Typography variant="body2" color="error" sx={{ my: 1 }}>
                {formErrors.general}
              </Typography>
            )}
          </form>
        </Box>

        {/* Save button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: '32px',
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formLoading}
            onClick={handleFormSubmit}
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: '17px',
              py: '8px',
              fontSize: '16px',
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
          >
            {formLoading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </>
    </CommonModal>
  )
}
